import axios from "axios";
import i18n from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "op_access_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); },
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Bearer token from localStorage as fallback when cookies are blocked
// (e.g. inside cross-site iframes / Safari ITP / strict cookie policies).
api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// ---- Auto-refresh on 401 ----
let refreshPromise = null;

async function tryRefresh() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API}/auth/refresh`, {}, {
        withCredentials: true,
        headers: tokenStore.get() ? { Authorization: `Bearer ${tokenStore.get()}` } : {},
      })
      .then((r) => {
        if (r.data?.access_token) tokenStore.set(r.data.access_token);
        return r;
      })
      .finally(() => {
        setTimeout(() => { refreshPromise = null; }, 100);
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthCall = /\/auth\/(login|register|refresh|logout|me)$/.test(url);

    if (status === 401 && !original.__retried && !isAuthCall) {
      original.__retried = true;
      try {
        await tryRefresh();
        return api(original);
      } catch {
        // fall through
      }
      window.dispatchEvent(new CustomEvent("op:session-expired"));
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return i18n.t("errors.generic");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function isAuthError(err) {
  return err?.response?.status === 401 || err?.response?.status === 403;
}

export default api;
